import type { ReactNode } from 'react';
import { useState, useEffect, useCallback, createContext, useContext } from 'react';

interface Bookmark {
  suttaId: string;
  title: string;
  titlePali?: string | undefined;
  nikaya: string;
  timestamp: number;
}

interface BookmarksContextType {
  bookmarks: Bookmark[];
  isBookmarked: (suttaId: string) => boolean;
  toggleBookmark: (sutta: Omit<Bookmark, 'timestamp'>) => void;
  removeBookmark: (suttaId: string) => void;
}

const STORAGE_KEY = 'kannadatipitaka-bookmarks';

const BookmarksContext = createContext<BookmarksContextType | null>(null);

function getStoredBookmarks(): Bookmark[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveBookmarks(bookmarks: Bookmark[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bookmarks));
  } catch (error) {
    console.error('Failed to save bookmarks:', error);
  }
}

export function BookmarksProvider({ children }: { children: ReactNode }) {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);

  // Load bookmarks on mount
  useEffect(() => {
    setBookmarks(getStoredBookmarks());
  }, []);

  const isBookmarked = useCallback(
    (suttaId: string) => {
      return bookmarks.some(b => b.suttaId === suttaId);
    },
    [bookmarks]
  );

  const toggleBookmark = useCallback((sutta: Omit<Bookmark, 'timestamp'>) => {
    setBookmarks(prev => {
      const exists = prev.some(b => b.suttaId === sutta.suttaId);

      let updated: Bookmark[];
      if (exists) {
        updated = prev.filter(b => b.suttaId !== sutta.suttaId);
      } else {
        updated = [{ ...sutta, timestamp: Date.now() }, ...prev];
      }

      saveBookmarks(updated);
      return updated;
    });
  }, []);

  const removeBookmark = useCallback((suttaId: string) => {
    setBookmarks(prev => {
      const updated = prev.filter(b => b.suttaId !== suttaId);
      saveBookmarks(updated);
      return updated;
    });
  }, []);

  return (
    <BookmarksContext.Provider value={{ bookmarks, isBookmarked, toggleBookmark, removeBookmark }}>
      {children}
    </BookmarksContext.Provider>
  );
}

export function useBookmarks() {
  const context = useContext(BookmarksContext);
  if (!context) {
    throw new Error('useBookmarks must be used within BookmarksProvider');
  }
  return context;
}

export type { Bookmark };
